import type { QuoteDocument } from 'paintscout'
import type { PainterAdditionalWork, PainterScheduleEvent } from '../PainterScheduleView'

export interface PainterAdditionalWorkOriginEntry {
  preset: string[]
  custom: string[]
}

export type PainterAdditionalWorkOriginIndex = Record<string, PainterAdditionalWorkOriginEntry>
export type PainterAdditionalWorkDeleteMode = 'move-to-options' | 'remove' | 'none'

export function applyPainterAdditionalWorkOrigins(
  event: PainterScheduleEvent,
  originIndex: PainterAdditionalWorkOriginIndex
): PainterScheduleEvent {
  const quoteId = event.sourceQuote?._id
  if (!quoteId || !event.additionalWork?.length) return event

  const entry = originIndex[quoteId]
  if (!entry) return event

  const preset = new Set(entry.preset)
  const custom = new Set(entry.custom)

  return {
    ...event,
    additionalWork: event.additionalWork.map((item) => {
      if (!item.sourceKey) return item
      if (custom.has(item.sourceKey)) return { ...item, isCreated: true }
      if (preset.has(item.sourceKey)) return { ...item, isCreated: false }
      return item
    })
  }
}

export function recordPainterAdditionalWorkOrigins(
  originIndex: PainterAdditionalWorkOriginIndex,
  quoteId: string | undefined,
  keys: string[],
  origin: keyof PainterAdditionalWorkOriginEntry
): PainterAdditionalWorkOriginIndex {
  if (!quoteId || keys.length === 0) return originIndex

  const entry = originIndex[quoteId] ?? { preset: [], custom: [] }
  const nextKeys = new Set(keys)

  return {
    ...originIndex,
    [quoteId]: {
      preset: origin === 'preset' ? uniq([...entry.preset, ...keys]) : entry.preset.filter((key) => !nextKeys.has(key)),
      custom: origin === 'custom' ? uniq([...entry.custom, ...keys]) : entry.custom.filter((key) => !nextKeys.has(key))
    }
  }
}

export function removePainterAdditionalWorkOrigins(
  originIndex: PainterAdditionalWorkOriginIndex,
  quoteId: string | undefined,
  keys: string[]
): PainterAdditionalWorkOriginIndex {
  if (!quoteId || keys.length === 0 || !originIndex[quoteId]) return originIndex

  const remove = new Set(keys)
  const entry = originIndex[quoteId]
  return {
    ...originIndex,
    [quoteId]: {
      preset: entry.preset.filter((key) => !remove.has(key)),
      custom: entry.custom.filter((key) => !remove.has(key))
    }
  }
}

export function isPainterAdditionalWorkSigned(item: PainterAdditionalWork, _quote?: QuoteDocument) {
  return item.status === 'approved' || item.approvedDate != null
}

export function getPainterAdditionalWorkCapabilities(item: PainterAdditionalWork, quote?: QuoteDocument) {
  if (isPainterAdditionalWorkSigned(item, quote)) {
    return {
      canEdit: false,
      canDelete: false,
      deleteMode: 'none' as PainterAdditionalWorkDeleteMode
    }
  }

  const isPending = item.status === 'pending' || item.sourceSection === 'pending' || !item.sourceSection
  if (!isPending) {
    return {
      canEdit: false,
      canDelete: false,
      deleteMode: 'none' as PainterAdditionalWorkDeleteMode
    }
  }

  if (!item.isCreated) {
    return {
      canEdit: false,
      canDelete: true,
      deleteMode: 'move-to-options' as PainterAdditionalWorkDeleteMode
    }
  }

  return {
    canEdit: true,
    canDelete: true,
    deleteMode: 'remove' as PainterAdditionalWorkDeleteMode
  }
}

export function getPainterAdditionalWorkDeleteMode(item: PainterAdditionalWork, quote?: QuoteDocument) {
  return getPainterAdditionalWorkCapabilities(item, quote).deleteMode
}

export function getPainterAdditionalWorkOriginsFromQuote(quote?: QuoteDocument) {
  if (!quote || Array.isArray(quote.additionalWork)) {
    return { preset: [], custom: [] } satisfies PainterAdditionalWorkOriginEntry
  }

  return {
    preset: quote.additionalWork?.painterOrigins?.preset ?? [],
    custom: quote.additionalWork?.painterOrigins?.custom ?? []
  } satisfies PainterAdditionalWorkOriginEntry
}

export function setPainterAdditionalWorkOriginsOnQuote(
  quote: QuoteDocument,
  updater: (entry: PainterAdditionalWorkOriginEntry) => PainterAdditionalWorkOriginEntry
): QuoteDocument {
  if (Array.isArray(quote.additionalWork)) return quote

  const nextOrigins = updater({
    preset: quote.additionalWork?.painterOrigins?.preset ?? [],
    custom: quote.additionalWork?.painterOrigins?.custom ?? []
  })

  return {
    ...quote,
    additionalWork: {
      ...quote.additionalWork,
      painterOrigins: nextOrigins
    }
  }
}

function uniq(values: string[]) {
  return Array.from(new Set(values))
}
